/**
 * Performance › Third parties (`/performance/third-parties`) — every
 * third-party script the audits found, ranked by what it costs.
 *
 * Promoted out of the Audits page's sub-tab row (2026-07-26). It reads
 * the same audit runs, but it answers a different question — which
 * vendors are costing you, not how this site or page is trending — and
 * it's site-level, so none of the Audits view's scope / device / region
 * filters apply to it. Sharing that page meant it sat under a filter bar
 * that couldn't touch it. The legacy `?sub=thirdparty` (and the older
 * `?view=thirdparty`) redirect here.
 */
import { CompareSitePicker, useCompareSites } from '@/components/primitives/CompareSitePicker';
import { SectionBoundary } from '@/components/primitives/SectionBoundary';
import { ThirdPartiesSection } from '@/components/rum/ThirdPartiesSection';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { DEMO_EMBED } from '@/lib/demo';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

interface ThirdPartiesSearch {
  /** Guest site uuid to compare against. Shareable, like the other
   *  planes' `?compare=`. */
  compare?: string;
}

export const Route = createFileRoute('/_app/performance/third-parties')({
  validateSearch: (search: Record<string, unknown>): ThirdPartiesSearch =>
    typeof search.compare === 'string' && search.compare !== '' ? { compare: search.compare } : {},
  component: ThirdPartiesPage,
});

function ThirdPartiesPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Third parties" parent={{ label: 'Performance', href: '/performance' }}>
      {siteUuid && <ThirdPartiesBody siteUuid={siteUuid} />}
    </SiteIsland>
  );
}

function ThirdPartiesBody({ siteUuid }: { siteUuid: string }): ReactElement {
  const { compare } = Route.useSearch();
  const navigate = Route.useNavigate();
  // No self option: third parties are site-level, so a same-site pick
  // would compare a site against itself.
  const { options: compareOptions, compareUuid, compareName } = useCompareSites(siteUuid, compare);

  return (
    <div className="space-y-4">
      {!DEMO_EMBED && compareOptions.length > 0 && (
        <div className="flex items-center justify-end">
          <CompareSitePicker
            value={compareUuid}
            options={compareOptions}
            onChange={(next) => void navigate({ search: next ? { compare: next } : {} })}
          />
        </div>
      )}
      <SectionBoundary label="this view">
        <ThirdPartiesSection
          siteUuid={siteUuid}
          enabled
          compareSiteUuid={compareUuid ?? undefined}
          compareName={compareUuid ? (compareName ?? 'Other site') : undefined}
        />
      </SectionBoundary>
    </div>
  );
}
